import _ from 'lodash'
import moment from 'moment'

/**
 * @param dataWrapper {ReturnType<import('core/data/data-wrapper')._DB>}
 * @param basicPlanRepository {ReturnType<import('core/data-model/basic-plan/repository').createBasicPlanRepository>}
 * @param riderRepository {ReturnType<import('core/data-model/rider/repository').createRiderRepository>}
 */
export function createCampaignRepository(dataWrapper, basicPlanRepository, riderRepository) {
  const { list, get } = dataWrapper
  const DOCUMENT_KEY = 'campaign'
  const DATE_FORMAT = 'DD/MM/YYYY'
  const override = (object, overrides) => {
    if (overrides == null || object == null) {
      return object
    } else {
      return { ...object, ...overrides }
    }
  }

  const _overrideRiderProps = (rawCampaign, rider, code) => {
    const riderOverrideProps = _.get(rawCampaign, `ridersOverrides.${code}`, null)
    if (riderOverrideProps && rider.code === 'MHP') {
      const riderPackage = rider.packageRiders.find((p) =>
        Object.values(_.get(p, 'codes.Thailand')).includes(riderOverrideProps.options[0].values[0].planCode)
      )
      riderOverrideProps.defaultSumAssured = riderPackage ? riderPackage.defaultSumAssured : rider.defaultSumAssured
    }
    return riderOverrideProps
  }

  const _mergeProperties = (rawCampaign, selectedCampaignPlan, distributorGroup = '', agentCode = '') => (
    // $FlowFixMe
    [basicPlan, requiredRiders, optionalRiders]
  ) => {
    let overriddenBasicPlan = override(basicPlan, rawCampaign.basicPlanOverrides)
    if (typeof selectedCampaignPlan !== 'undefined' && selectedCampaignPlan != '') {
      const overriddenCampaignPlan = _.filter(rawCampaign.basicPlanOverrides.plans, { planCode: selectedCampaignPlan })
      if (overriddenCampaignPlan.length > 0 && typeof overriddenCampaignPlan[0].basicPlanOverrides !== 'undefined') {
        overriddenBasicPlan = override(overriddenBasicPlan, overriddenCampaignPlan[0].basicPlanOverrides)
      }
    }

    const overriddenRequiredRiders = requiredRiders.map((rider) =>
      override(
        basicPlanRepository._overrideRiderByUserType(rider, distributorGroup),
        _overrideRiderProps(
          rawCampaign,
          basicPlanRepository._overrideRiderByUserType(rider, distributorGroup),
          rider.code
        )
      )
    )
    const overriddenOptionalRiders = optionalRiders.map((rider) =>
      override(
        basicPlanRepository._overrideRiderByUserType(rider, distributorGroup),
        _overrideRiderProps(
          rawCampaign,
          basicPlanRepository._overrideRiderByUserType(rider, distributorGroup),
          rider.code
        )
      )
    )

    overriddenBasicPlan.riders = overriddenRequiredRiders.concat(overriddenOptionalRiders)
    return {
      code: rawCampaign.code,
      type: rawCampaign.type,
      name: rawCampaign.name,
      nameThai: rawCampaign.nameThai,
      fullNameThai: rawCampaign.fullNameThai,
      displayName: rawCampaign.displayName,
      category: rawCampaign.category,
      image: rawCampaign.image,
      description: rawCampaign.description,
      benefits: rawCampaign.benefits,
      startDate: rawCampaign.startDate,
      endDate: rawCampaign.endDate,
      basicPlan: overriddenBasicPlan,
      displayNameLanguage: rawCampaign.displayNameLanguage,
      selectedCampaignPlan: rawCampaign.selectedCampaignPlan,
      readOnlyFields: overriddenBasicPlan.readOnlyFields,
      riders: {
        required: overriddenRequiredRiders,
        optional: overriddenOptionalRiders,
      },
      licenses: rawCampaign.licenses,
      ribbonMessage: rawCampaign.ribbonMessage,
      payerSelection: rawCampaign.payerSelection,
      allowUserType: rawCampaign.allowUserType,
      agentOverrides: rawCampaign.agentOverrides,
      bancOverrides: rawCampaign.bancOverrides,
      userOverrides: rawCampaign.userOverrides,
      userOverridesList: rawCampaign.userOverridesList,
      userOverridesEffectiveDate: rawCampaign.userOverridesEffectiveDate,
      userOverridesExpiryDate: rawCampaign.userOverridesExpiryDate,
      defaultSumAssured: rawCampaign.defaultSumAssured,
      basicPlanCodeOption: rawCampaign.basicPlanCodeOption,
      brochureLink: rawCampaign.brochureLink,
      luckyNumberPolicy: rawCampaign.luckyNumberPolicy,
      pensionAge: rawCampaign.pensionAge,
    }
  }

  const _hydrateCampaign = (
    getBasicPlan,
    getRider,
    rawCampaign,
    selectedCampaignPlan,
    distributorGroup = '',
    agentCode = ''
  ) => {
    const getBasicPlanPromise = getBasicPlan(rawCampaign.basicPlanCode, distributorGroup, agentCode)
    const getRequiredRidersPromise = Promise.all(rawCampaign.riders.required.map(getRider))
    const getOptionalRidersPromise = Promise.all(rawCampaign.riders.optional.map(getRider))
    const promises = [getBasicPlanPromise, getRequiredRidersPromise, getOptionalRidersPromise]
    return Promise.all(promises).then(_mergeProperties(rawCampaign, selectedCampaignPlan, distributorGroup, agentCode))
  }

  const _getCampaign = (get, code) => get(`${DOCUMENT_KEY}_${code}`)

  const getCampaign = (code, selectedCampaignPlan, distributorGroup = '', agentCode = '') =>
    _getCampaign(get, code).then((rawCampaign) =>
      _hydrateCampaign(
        basicPlanRepository.getBasicPlan,
        riderRepository.getRider,
        rawCampaign,
        selectedCampaignPlan,
        distributorGroup,
        agentCode
      )
    )

  const _listCampaigns = (list) => list(DOCUMENT_KEY)

  const listCampaigns = () =>
    _listCampaigns(list).then((rawCampaigns) =>
      Promise.all(
        rawCampaigns.map((rawCampaign) =>
          _hydrateCampaign(basicPlanRepository.getBasicPlan, riderRepository.getRider, rawCampaign)
        )
      )
    )

  const _listActiveCampaigns = (listCampaigns) =>
    listCampaigns().then((campaigns) =>
      campaigns.filter((campaign) =>
        moment().isBetween(moment(campaign.startDate, DATE_FORMAT), moment(campaign.endDate, DATE_FORMAT), null, '[]')
      )
    )

  const listActiveCampaigns = () => _listActiveCampaigns(listCampaigns)

  return {
    _mergeProperties,
    _hydrateCampaign,
    _getCampaign,
    getCampaign,
    _listCampaigns,
    listCampaigns,
    _listActiveCampaigns,
    listActiveCampaigns,
  }
}
